import { useEffect, useState } from 'react';

/** What the interviewer says it's doing while the next question is prepared.
 *  Always opens on "Thinking" so short waits read exactly as before; longer
 *  waits rotate through the rest. Kept short so the cue under the orb stays on
 *  one line on narrow phones. */
export const THINKING_PHRASES = [
  'Thinking',
  'Listening back',
  'Reflecting',
  'Mulling it over',
  'Connecting the dots',
  'Gathering thoughts',
  'Taking notes',
  'Finding the next question',
] as const;

/** What the start screen says between Start and the first word, so the wait
 *  reads as the interview getting ready rather than a stalled spinner. */
export const STARTING_PHRASES = [
  'Starting your interview',
  'Warming up the mic',
  'Pulling up your questions',
  'Almost ready',
] as const;

/** The start screen's phrases for a Fun & Games game. */
export const GAME_STARTING_PHRASES = [
  'Starting your game',
  ...STARTING_PHRASES.slice(1),
] as const;

/** How long each phrase stays up before the next one fades in. */
export const THINKING_PHRASE_MS = 2200;

/** The phrase of `phrases` shown `elapsedMs` into a wait. Cycles back past
 *  the first rather than stalling on the last phrase. */
export const phraseAt = (phrases: readonly string[], elapsedMs: number): string => {
  const step = Math.max(0, Math.floor(elapsedMs / THINKING_PHRASE_MS));
  if (step === 0 || phrases.length < 2) return phrases[0];
  const rest = phrases.length - 1;
  return phrases[1 + ((step - 1) % rest)];
};

/** The thinking phrase shown `elapsedMs` into a wait. */
export const thinkingPhraseAt = (elapsedMs: number): string => phraseAt(THINKING_PHRASES, elapsedMs);

/** Rotates through `phrases` while `active`, restarting from the first each
 *  time a new wait begins. */
function useRotatingPhrase(phrases: readonly string[], active: boolean): string {
  const [step, setStep] = useState(0);
  useEffect(() => {
    setStep(0);
    if (!active) return;
    const id = window.setInterval(() => setStep(s => s + 1), THINKING_PHRASE_MS);
    return () => window.clearInterval(id);
  }, [active]);
  return phraseAt(phrases, step * THINKING_PHRASE_MS);
}

/** Rotates through THINKING_PHRASES while `active`, restarting from
 *  "Thinking" each time a new wait begins. */
export function useThinkingPhrase(active: boolean): string {
  return useRotatingPhrase(THINKING_PHRASES, active);
}

/** Rotates through STARTING_PHRASES (a game's, for a game) while the interview starts. */
export function useStartingPhrase(active: boolean, game = false): string {
  return useRotatingPhrase(game ? GAME_STARTING_PHRASES : STARTING_PHRASES, active);
}
